import React from "react";

export default function HeroImage({
  slides = [],
  onCtaClick,
  aspectRatio = "hero",
}) {
  // Only use the first slide
  const slide = slides[0];

  if (!slides.length) return null;

  const isHero = aspectRatio === "hero";

  return (
    <div
      className={`hero-container ${isHero ? "hero-fullscreen" : "hero-property"}`}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: isHero ? "0" : "12px",
        height: isHero ? "100vh" : "420px",
        minHeight: isHero ? "600px" : "300px",
        width: "100%",
      }}
    >
      <style>{`
        /* Keep the hero a horizontal/landscape band on phones instead of
           stretching to a tall portrait 100vh block */
        @media (max-width: 640px) {
          .hero-fullscreen {
            height: 56vw !important;      /* landscape ratio, roughly 16:9 */
            min-height: 280px !important;
            max-height: 420px !important;
          }
          /* Show the FULL video (no cropping/zoom) instead of cover-cropping
             a wide banner down to a short mobile band */
          .hero-fullscreen .hero-video {
            object-fit: contain !important;
          }
          .hero-fullscreen .hero-headline {
            font-size: clamp(1.4rem, 6vw, 2rem) !important;
            margin-bottom: 8px !important;
          }
          .hero-fullscreen .hero-subheadline {
            font-size: clamp(0.85rem, 3vw, 1rem) !important;
            margin-bottom: 16px !important;
            -webkit-line-clamp: 2;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .hero-fullscreen .hero-cta-btn {
            padding: 10px 26px !important;
            font-size: 0.85rem !important;
          }
        }
        @media (max-width: 400px) {
          .hero-fullscreen {
            height: 60vw !important;
            min-height: 260px !important;
            max-height: 360px !important;
          }
        }
      `}</style>

      {/* Background Video */}
      <div
        className="hero-video-wrapper"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          background: "#F7FBF8", // fills any letterbox space on mobile so it blends in
        }}
      >
        <video
          className="hero-video"
          src={slide.src}
          poster={slide.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-label={slide.headline || "Hero Video"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>

      {/* Hero Content Overlay - No Black Background */}
      {isHero && slide.headline && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 24px",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {/* Headline */}
          <p
            className="hero-headline"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 5vw, 4rem)",
              fontWeight: 600,
              color: "#FFFFFF",
              lineHeight: 1.2,
              maxWidth: "800px",
              marginBottom: "16px",
              textShadow: "0 2px 20px rgba(0,0,0,0.5)",
              pointerEvents: "none",
            }}
          >
            {slide.headline}
          </p>

          {/* Subheadline */}
          <p
            className="hero-subheadline"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "clamp(1rem, 2vw, 1.25rem)",
              color: "rgba(255,255,255,0.9)",
              maxWidth: "600px",
              marginBottom: "32px",
              lineHeight: 1.6,
              pointerEvents: "none",
              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
            }}
          >
            {slide.subheadline}
          </p>

          {/* CTA Button */}
          {slide.cta && onCtaClick && (
            <button
              onClick={onCtaClick}
              className="hero-cta-btn"
              style={{
                background: "linear-gradient(135deg, #50C878, #7FD9A0)",
                color: "#FFFFFF",
                border: "none",
                padding: "14px 40px",
                borderRadius: "4px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "1rem",
                cursor: "pointer",
                letterSpacing: "0.05em",
                transition: "transform 0.2s, box-shadow 0.2s",
                pointerEvents: "auto",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(80,200,120,0.5)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {slide.cta}
            </button>
          )}
        </div>
      )}

      {/* Property Mode Content (non-hero) - No Black Background */}
      {!isHero && slide.headline && (
        <div
          style={{
            position: "absolute",
            bottom: "40px",
            left: "40px",
            right: "40px",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <h3
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#FFFFFF",
              marginBottom: "8px",
              textShadow: "0 2px 10px rgba(0,0,0,0.5)",
            }}
          >
            {slide.headline}
          </h3>
          {slide.subheadline && (
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "rgba(255,255,255,0.9)",
                textShadow: "0 2px 10px rgba(0,0,0,0.5)",
              }}
            >
              {slide.subheadline}
            </p>
          )}
          {slide.cta && onCtaClick && (
            <button
              onClick={onCtaClick}
              style={{
                background: "linear-gradient(135deg, #50C878, #7FD9A0)",
                color: "#FFFFFF",
                border: "none",
                padding: "10px 30px",
                borderRadius: "4px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: "pointer",
                marginTop: "12px",
                transition: "transform 0.2s, box-shadow 0.2s",
                pointerEvents: "auto",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(80,200,120,0.5)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {slide.cta}
            </button>
          )}
        </div>
      )}
    </div>
  );
}